import React, { useEffect, useState } from 'react'
import styles from './index.module.css'
import { ISubscriptionArgs, ISubscriptionInfo, IVehicle, IVehiclePriceGroup } from '../../../../reducers/bookings/types';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { Card, CardActions, CardContent, CardHeader, CardMedia, Fab, Grid, IconButton, Theme, Typography, createStyles, makeStyles } from '@material-ui/core';
import { getFeatureTypeIcon, getVehicleTypeIcon, toCurrency } from '../../../utils';
import { RATE_TYPES, capitalizeFirstLetter } from '../../utils';
import SelectSubscriptionPricingView from './SelectSubscriptionPricingView';
import { useSelector } from 'react-redux';
import { IAppState } from '../../../../store';
import { Storage } from "aws-amplify";
import { useSnackBar } from '../../../SnackBarContext/SnackBarContext';
import { SnackBarVariant } from '../../../SnackbarWrapper/SnackbarWrapper';
import { getSignedUrl } from '../../../../utils/getSignedUrl';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    media: {
      height: 0, // 16:9
      backgroundSize: "contain",
      paddingTop: "25%",
      marginTop: "2rem"
    },
    cardHeader: {
      background: "#55c2da",
      color: "#fff",
      textAlign: "center"
    },
    actionBtn: {
      backgroundImage: "linear-gradient(144deg,#AF40FF, #5B42F3 50%,#00DDEB)"
    },
    subButn: {
      background: "none",
      border: "none",
      boxShadow: "none",
      fontSize: 16,
      color: "#fff",
      width: "100%",
      height: 0,
      "&:hover": {
        background: "linear-gradient(144deg,#AF40FF, #5B42F3 50%,#00DDEB)",
      }
    }
  }),
);

interface IProps {
  subscriptionVehicles: IVehicle[];
  bookingDurationInMonths: number;
  bookingDurationInWeeks: number;
  onSelectSubscription: (subscription: ISubscriptionArgs, vehicle: IVehicle) => void;
  showGridForSubscription: boolean;
}

const SelectSubscriptionVehicleView: React.FC<IProps> = (props) => {

  const classes = useStyles();
  const snackbar = useSnackBar();
  const [open, setOpen] = useState<boolean>(false);
  const [selectedVehicle, setSelectedVehicle] = useState<IVehicle>();
  const [vehicleImages, setVehicleImages] = useState<any>([]);
  const [noVehiclesFoundError, setNoVehiclesFoundError] = useState<boolean>(false)
  const [vehicles, setVehicles] = useState<IVehicle[]>([])
  const {t} = useTranslation();
  const website = useSelector((state: IAppState) => state.consumerWebsiteReducer.consumerWebsite);
  const { locale, currency } = website.organisation

  useEffect(() => {
    if (props.subscriptionVehicles && props.subscriptionVehicles.length) {
      getVehicleImage(props.subscriptionVehicles)
      setVehicles(props.subscriptionVehicles)
    }
  }, [props.subscriptionVehicles])

  const getVehicleImage = async (vehicles: IVehicle[]) => {
    try {
      const imagePromises = vehicles.map(async (vehicle) => {
        if (vehicle && vehicle.imageUrl) {
          try {
            const file = await getSignedUrl(vehicle.imageUrl).then((result) => {
              return result;
            });
            return {
              vehicleId: vehicle.id,
              imageUrl: file,
            };
          } catch (urlError) {
            snackbar({
              message: t('snackBar:errorFetchingSignedUrl'),
              variant: SnackBarVariant.ERROR,
            });
            return null;
          }
        }
        return null;
      });
      const resolvedImages = await Promise.all(imagePromises);
      const validImages = resolvedImages.filter((image) => image !== null);
      setVehicleImages(validImages)
    } catch (error) {
      snackbar({
        message: t("snackBar:invalidFileType"),
        variant: SnackBarVariant.ERROR
      });
    }
  };

  const getLowestSubscriptionPricing = (vehicles: IVehicle[]) => {
    const subscriptions: ISubscriptionInfo[] = [];
    vehicles.forEach(vehicle => {
      if (vehicle.subscriptions?.length) {
        subscriptions.push(...vehicle.subscriptions)
      }
    })
    let pricings: any[] = [];
    subscriptions.forEach(subscription => {
      if (subscription.subscription.isActive) {
        subscription.pricings.forEach(pricing => {
          if ((subscription.subscription.rateTypeName === RATE_TYPES.MONTHLY && pricing.duration <= props.bookingDurationInMonths) || (subscription.subscription.rateTypeName === RATE_TYPES.WEEKLY && pricing.duration <= props.bookingDurationInWeeks)) {
            pricings.push(pricing)
          }
        })
      }
    })
    const lowestPricing = Math.min(...pricings.map(({ subscriptionAmount }) => subscriptionAmount));
    return lowestPricing
  }
  return (
    <Grid item xs={12} container spacing={2}>
      {
        vehicles && vehicles.length > 0 &&
        vehicles.map((vehicle: IVehicle, index: number) => {
          if (vehicleImages && vehicleImages.length) {
            vehicleImages.forEach((vehicleImage: any, index: number) => {
              if (vehicleImage.imageUrl && vehicleImage.vehicleId === vehicle.id) {
                vehicle = {
                  ...vehicle,
                  imageUrl: vehicleImage.imageUrl
                }
              }
            });
          }
          if (vehicle && vehicle.subscriptions.length) {
            const lowestPrice = getLowestSubscriptionPricing([vehicle])
            if (lowestPrice !== Infinity) {
              return (
                <Grid
                  key={index}
                  item
                  xs={12}
                  sm={6}
                  md={6}
                  lg={props.showGridForSubscription ? 4 : 6}
                  xl={props.showGridForSubscription ? 4 : 6}
                >
                  <Card
                    style={{ borderRadius: 14 }}
                  >
                    <CardHeader
                      className={classes.cardHeader}
                      action={
                        <IconButton aria-label="settings">

                        </IconButton>
                      }
                      title={<Typography variant='h4' style={{ fontSize: 20 }}>{`${vehicle.make.toUpperCase()} ${vehicle.model.toUpperCase()}`}</Typography>}
                      subheader={<Typography variant='caption'></Typography>}
                    />
                    <CardContent>
                      <Grid item xs={12} container justify='center'>
                        {
                          vehicle.imageUrl ? (

                            <img
                              src={vehicle.imageUrl}
                              alt= {t('root:vehicle')}
                              style={{ width: "100%", height: "200px" }}
                            />
                          ) : (
                            <img
                              src={getVehicleTypeIcon(capitalizeFirstLetter(vehicle.bodyType))}
                              alt= {t('root:vehicle')}
                              style={{ width: "100%", height: "200px" }}
                            />
                          )
                        }
                      </Grid>
                      <Grid container item xs={12} spacing={2}>
                        <Grid item xs={6}>
                          <img
                            style={{
                              height: "12px",
                              marginRight: "10px",
                              width: "16px",
                            }}
                            src={getFeatureTypeIcon("numberOfDoors")}
                            alt={t("root:numberOfDoors")}
                          />
                          <span style={{ fontSize: 12 }}>{`${vehicle.numberOfDoors} Doors`}</span>
                        </Grid>
                        <Grid item xs={6}>
                          <img
                            style={{
                              height: "12px",
                              marginRight: "10px",
                              width: "16px",
                            }}
                            src={getFeatureTypeIcon("numberOfSeats")}
                            alt={t("root:numberOfSeats")}
                          />
                          <span style={{ fontSize: 12 }}>{`${vehicle.numberOfSeats} Seats`}</span>
                        </Grid>
                        <Grid item xs={6}>
                          <img
                            style={{
                              height: "12px",
                              marginRight: "10px",
                              width: "16px",
                            }}
                            src={getFeatureTypeIcon("transmission")}
                            alt={t("root:transmission")}
                          />
                          <span style={{ fontSize: 12 }}>{`${vehicle.transmission}`}</span>
                        </Grid>
                        <Grid item xs={6}>
                          <img
                            style={{
                              height: "12px",
                              marginRight: "10px",
                              width: "16px",
                            }}
                            src={getFeatureTypeIcon("fuelType")}
                            alt={t("root:fuelType")}
                          />
                          <span style={{ fontSize: 12 }}>{`${vehicle.fuelType}`}</span>
                        </Grid>
                      </Grid>
                    </CardContent>
                    <CardActions className={classes.actionBtn} disableSpacing>
                      <Grid item xs={12} container justify='center'>
                        <Fab
                          className={`${classes.subButn} test `}
                          onClick={() => {
                            setSelectedVehicle(vehicle)
                            setOpen(true)
                          }}
                        >
                          {
                            props.bookingDurationInMonths > 0 && vehicle.subscriptions.some((subscription) => subscription.pricings.some(pricing => (subscription.subscription.rateTypeName === RATE_TYPES.MONTHLY && pricing.duration <= props.bookingDurationInMonths) || (subscription.subscription.rateTypeName === RATE_TYPES.WEEKLY && pricing.duration <= props.bookingDurationInWeeks))) && (
                              <Typography variant="h3" style={{ fontWeight: 600 }}>{t("root:subscriptionStartsAt")} {toCurrency(lowestPrice, currency, locale)}</Typography>
                            )
                          }
                        </Fab>
                      </Grid>
                    </CardActions>
                  </Card>
                </Grid>
              )
            }
          }
        })
      }
      {noVehiclesFoundError && <div className={styles.vehicleGroupcard} style={{ padding: 15, border: 0 }}>{t("root:noVehiclesFound")}</div>}
      {
        open && (
          <SelectSubscriptionPricingView
            open={open}
            bookingDurationInMonths={props.bookingDurationInMonths || 0}
            bookingDurationInWeeks={props.bookingDurationInWeeks || 0}
            onCancel={() => setOpen(false)}
            setOpen={setOpen}
            onSelectSubscription={props.onSelectSubscription}
            vehicle={selectedVehicle}
          />
        )
      }
    </Grid>
  )
}

export default SelectSubscriptionVehicleView
